import React from 'react'
import styled from 'styled-components'
import type { TFunction } from 'i18next'
import { Icon } from '../../../../../../atoms/Icon'
import { ExpandArrow } from '../../../../../../atoms/ExpandArrow'
import { UnitsTable } from './UnitsTable'
import type { ProcessedEntrance } from '../types'

const EntranceCardContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  overflow: hidden;
  transition: all ${({ theme }) => theme.transitions.default};

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.tertiary};
    box-shadow: ${({ theme }) => theme.shadows.sm};
  }
`

const EntranceHeader = styled.div`
  padding: ${({ theme }) => theme.spacing.medium}
    ${({ theme }) => theme.spacing.large};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.default}
      ${({ theme }) => theme.spacing.medium};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.default}
      ${({ theme }) => theme.spacing.default};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.default};
    align-items: flex-start;
  }
`

const EntranceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
  flex: 1;

  @media (max-width: 768px) {
    gap: ${({ theme }) => theme.spacing.default};
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`

const EntranceIcon = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(0, 173, 230, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
`

const EntranceDetails = styled.div`
  flex: 1;
`

const EntranceTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 2px;

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSize.body};
  }
`

const EntranceMeta = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.default};

  @media (max-width: 768px) {
    font-size: ${({ theme }) => theme.typography.fontSize.label};
    gap: ${({ theme }) => theme.spacing.small};
  }

  @media (max-width: 480px) {
    gap: ${({ theme }) => theme.spacing.xsmall};
    flex-wrap: wrap;
  }
`

const MetaDivider = styled.div`
  width: 1px;
  height: 12px;
  background: ${({ theme }) => theme.colors.border.default};
`

const EntranceContent = styled.div<{ $expanded: boolean }>`
  display: ${({ $expanded }) => ($expanded ? 'block' : 'none')};
`

interface EntranceCardProps {
  entrance: ProcessedEntrance
  expanded: boolean
  onToggleEntrance: (entranceId: string) => void
  t: TFunction
}

export const EntranceCard: React.FC<EntranceCardProps> = ({
  entrance,
  expanded,
  onToggleEntrance,
  t,
}) => {
  const floorsCount = entrance.floors.length
  const unitsCount = entrance.totalUnits
  const floorsLabel =
    floorsCount === 1
      ? t('unitDetails.entrance.floorsCountOne', { count: floorsCount })
      : t('unitDetails.entrance.floorsCount', { count: floorsCount })
  const unitsLabel =
    unitsCount === 1
      ? t('unitDetails.entrance.unitsCountOne', { count: unitsCount })
      : t('unitDetails.entrance.unitsCount', { count: unitsCount })

  return (
    <EntranceCardContainer>
      <EntranceHeader onClick={() => onToggleEntrance(entrance.id)}>
        <EntranceInfo>
          <EntranceIcon>
            <Icon name="building" size="small" />
          </EntranceIcon>
          <EntranceDetails>
            <EntranceTitle>{entrance.title}</EntranceTitle>
            <EntranceMeta>
              {entrance.egid && (
                <>
                  <span>
                    {t('unitDetails.entrance.egid')} {entrance.egid}
                  </span>
                  <MetaDivider />
                </>
              )}
              {entrance.address && (
                <>
                  <span>{entrance.address}</span>
                  <MetaDivider />
                </>
              )}
              <span>
                {floorsLabel} &bull; {unitsLabel}
              </span>
            </EntranceMeta>
          </EntranceDetails>
        </EntranceInfo>
        <ExpandArrow $expanded={expanded} direction="down" />
      </EntranceHeader>

      <EntranceContent $expanded={expanded}>
        <UnitsTable floors={entrance.floors} t={t} />
      </EntranceContent>
    </EntranceCardContainer>
  )
}
